import apiClient from "../utils/apiClient";
import logger from "../utils/logger";

class RecommendationsService {
  constructor() {
    this.serviceName = "RecommendationsService";
    this.baseUrl = "/api/casino/recommendations";
  }

  /**
   * Get personalized lobby recommendations for the authenticated user
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Recommendations data
   */
  async getLobbyRecommendations(options = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/lobby`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error fetching lobby recommendations:`,
        error,
      );
      throw this._handleError(error, "Failed to fetch lobby recommendations");
    }
  }

  /**
   * Get general lobby recommendations (not user-specific)
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Recommendations data
   */
  async getGeneralRecommendations(options = {}) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/general`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      logger.error(
        `[${this.serviceName}] Error fetching general recommendations:`,
        error,
      );
      throw this._handleError(error, "Failed to fetch general recommendations");
    }
  }

  /**
   * Get recommendations based on user authentication status
   * @param {boolean} isLoggedIn - Whether user is logged in
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Recommendations data
   */
  async getRecommendations(isLoggedIn, options = {}) {
    if (isLoggedIn) {
      return this.getLobbyRecommendations(options);
    } else {
      return this.getGeneralRecommendations(options);
    }
  }

  /**
   * Handle API errors
   * @private
   */
  _handleError(error, defaultMessage) {
    if (error.response) {
      // Server responded with error status
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        defaultMessage;
      return new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      return new Error("Network error - please check your connection");
    } else {
      // Something else happened
      return new Error(error.message || defaultMessage);
    }
  }
}

export default new RecommendationsService();
